import React, { type RefObject, useCallback, useMemo, useRef } from 'react';
import { isValidElement } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import type { TriggerProps } from '@atlaskit/popup/types';
import {
	ButtonMenuItem,
	type ButtonMenuItemProps,
} from '@atlaskit/side-nav-items/button-menu-item';
import { token } from '@atlaskit/tokens';
import { PopupTrigger } from '@atlassian/entry-points/popup-trigger';

import { GLOBAL_MORE_MENU_INTERACTION_NAME } from '../../common/constants';
import type { MoreMenuPopupProps } from '../../common/types';
import { PopupDataProvider } from '../../controllers/popup-data-provider';
import { useStandaloneButtonMenuItem } from '../../controllers/use-standalone-button-menu-item';

import { popupEntryPoint } from './content/popup.entrypoint';
import { messages } from './messages';

const styles = cssMap({
	popup: {
		// The More menu needs to be 4px wider than it's parent, on the left and right edges.
		// So extend the width by 8px, then use the PopupTrigger.offset property to shift
		// the panel 4px to the left, to center the content.
		// @ts-ignore - we want this property as it is written.
		width: `calc(100% + ${token('space.100')})`,
	},
});

export function MoreMenuPopup(props: MoreMenuPopupProps): React.JSX.Element {
	const { customizeSidebarMenuItem, showMoreMenu = true } = props;

	// This ref is ultimately passed to the Customization Modal, which should cause the modal dialog to
	// return focus to either the Customize Button or the More Menu trigger element.
	// This component is unusual because it must change the
	// visible-menuitem based on whether the more menu needs to show or not.
	const returnFocusRef = useRef<HTMLElement>(null);

	// customizeSidebarMenuItemWithRef adds a ref to the customizeSidebarMenuItem (to allow focussing)
	// and a returnFocusRef, which is passed to the Modal. It doesn't make sense for the caller to
	// define the ref, so *we* add the ref to the component dynamically.
	// The component is then rendered either within the More Menu (when showMoreMenu is true),
	// or as a standalone-button.
	// The isValidElement() test is a formality - it should always pass because GSN should always pass a
	// proper customizeSidebarMenuItem.
	const customizeSidebarMenuItemWithRef = useMemo(() => {
		return isValidElement(customizeSidebarMenuItem)
			? React.cloneElement(customizeSidebarMenuItem, { returnFocusRef, ref: returnFocusRef })
			: customizeSidebarMenuItem;
	}, [customizeSidebarMenuItem]);

	if (showMoreMenu) {
		return (
			<MoreMenuTriggerInner
				{...props}
				customizeSidebarMenuItem={customizeSidebarMenuItemWithRef}
				returnFocusRef={returnFocusRef}
			/>
		);
	}

	return (
		<CustomizeSidebarTriggerStandalone
			returnFocusRef={returnFocusRef}
			customizeSidebarMenuItem={customizeSidebarMenuItem}
		/>
	);
}

type CustomizeSidebarTriggerStandaloneProps = Pick<
	MoreMenuPopupProps,
	'customizeSidebarMenuItem'
> & {
	returnFocusRef: RefObject<HTMLElement>;
};

function CustomizeSidebarTriggerStandalone({
	customizeSidebarMenuItem,
	returnFocusRef,
}: CustomizeSidebarTriggerStandaloneProps) {
	return useStandaloneButtonMenuItem({
		item: customizeSidebarMenuItem,
		otherProps: {
			returnFocusRef,
			ref: returnFocusRef,
		},
	});
}

type MoreMenuTriggerButtonProps = ButtonMenuItemProps & {
	mergedRefs: React.Ref<HTMLButtonElement>;
};

function MoreMenuTriggerButton({ children, mergedRefs, ...props }: MoreMenuTriggerButtonProps) {
	return useStandaloneButtonMenuItem({
		item: (
			<ButtonMenuItem ref={mergedRefs} {...props}>
				{children}
			</ButtonMenuItem>
		),
	});
}

interface MoreMenuPopupPropsInner extends MoreMenuPopupProps {
	returnFocusRef: RefObject<HTMLElement>;
}

function MoreMenuTriggerInner(props: MoreMenuPopupPropsInner): React.JSX.Element {
	const {
		appItemsData,
		customizeSidebarMenuItem,
		dropIndicator,
		menuItemComponent,
		moreMenuItemLabel,
		onClickMoreMenuTrigger,
		productItemsData,
		returnFocusRef,
		testId,
		triggerRef,
		triggerVisualContentRef,
	} = props;
	const { formatMessage } = useIntl();

	const handleTriggerClick = useCallback(() => {
		onClickMoreMenuTrigger?.();
	}, [onClickMoreMenuTrigger]);

	const triggerFn = useCallback(
		(triggerProps: TriggerProps) => {
			const { ref, ...restTriggerProps } = triggerProps;
			return (
				<MoreMenuTriggerButton
					{...restTriggerProps}
					mergedRefs={mergeRefs(
						triggerRef ? [triggerRef, ref, returnFocusRef] : [ref, returnFocusRef],
					)}
					dropIndicator={dropIndicator}
					elemBefore={<ShowMoreHorizontalIcon label="" color="currentColor" spacing="spacious" />}
					isSelected={triggerProps['aria-expanded']}
					interactionName={GLOBAL_MORE_MENU_INTERACTION_NAME}
					onClick={handleTriggerClick}
					testId={testId}
					visualContentRef={triggerVisualContentRef}
				>
					{moreMenuItemLabel ?? formatMessage(messages.moreMenuTriggerLabel)}
				</MoreMenuTriggerButton>
			);
		},
		[
			dropIndicator,
			formatMessage,
			handleTriggerClick,
			moreMenuItemLabel,
			returnFocusRef,
			testId,
			triggerRef,
			triggerVisualContentRef,
		],
	);

	const entryPointProps = useMemo(
		() => ({
			customizeSidebarMenuItem,
			menuItemComponent,
		}),
		[customizeSidebarMenuItem, menuItemComponent],
	);

	return (
		<PopupDataProvider appItemsData={appItemsData} productItemsData={productItemsData}>
			<PopupTrigger
				entryPoint={popupEntryPoint}
				entryPointProps={entryPointProps}
				// Offset the top margin of the menu section.
				// This will keep an even spacing of menu items between those inside the dropdown and those above,
				// and keep the icons aligned
				offset={[-4, 4]}
				placement="bottom-start"
				preloadOnFocus
				role={undefined}
				shouldFitContainer
				shouldRenderToParent
				strategy="absolute"
				testId={`${testId || 'more-menu'}-content`}
				trigger={triggerFn}
				// It is very difficult to please TS here.  Tried using StrictXCSSProp<...>
				xcss={styles.popup as any}
			/>
		</PopupDataProvider>
	);
}
